/**
 * Which checks of a child layer (e.g. a pallet) a table or report row shows, seen from a parent layer
 * (e.g. a sales order). Shared by the children table on the layer page (show_children / show_tabs) and
 * the pallet table of dynamic-section PDF reports, so both pick the same check (EFQ-4298, EFQ-4339).
 *
 * 1. Preferred: checks at the preferred check locations; with `onlyForLayer`, only those done for `layer`.
 * 2. Fallback, only when a child has no preferred check: checks at the fallback check locations within
 *    the max age (`fallbackMaxAgeDays`, per child), not filtered by parent (e.g. the intake check belongs to the incoming order),
 *    but only at the layer's facility when the layer has one.
 */
export interface CheckSelection {
    preferred?: string[] | null;
    fallback?: string[] | null;
    fallbackMaxAgeDays?: FallbackMaxAgeSetting;
    onlyForLayer?: boolean;
    layer?: any;
    // * The child layers, to pick the max age per child (by its display filter)
    children?: any[] | null;
    // * The tenant's display filter (config.applyDisplayFilterFields), needed for a max age per display filter
    applyDisplayFilterFields?: ApplyDisplayFilterFields;
    // * Facility of a check (from its location_id), undefined when unknown
    facilityOf?: (check: any) => string | null | undefined;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * fallback_check_max_age_days: one number for every child, or a list where the first item whose
 * display_filter matches the child applies (fruit type, layer type, meta conditions, ...):
 *   [{ display_filter: { fruit_types: ["avocado"] }, field: { max_age_days: 3 } }]
 * Older report configs: [{ fruit_type: "avocado", max_age_days: 3 }]. No match: no limit.
 */
export type FallbackMaxAgeSetting = number | string | any[] | null | undefined;
export type ApplyDisplayFilterFields = (fields: any[], filter: any) => any[];

// * The legacy { fruit_type, max_age_days } items as display-filtered items
export const toFallbackMaxAgeItems = (setting: any[] | null | undefined): any[] => (setting ?? []).map((item) => (
    item && !item.display_filter && !item.field && "fruit_type" in item
        ? { display_filter: { fruit_types: item.fruit_type ? [item.fruit_type] : [] }, field: { max_age_days: item.max_age_days } }
        : item
));

const toDays = (value: any): number | null => (value === null || value === undefined || value === "" || Number.isNaN(Number(value)) ? null : Number(value));

export const getFallbackMaxAgeDays = (setting: FallbackMaxAgeSetting, child: any, applyDisplayFilterFields?: ApplyDisplayFilterFields): number | null => {
    if (!Array.isArray(setting)) return toDays(setting);
    if (!applyDisplayFilterFields) return null;
    const [match] = applyDisplayFilterFields(toFallbackMaxAgeItems(setting), { fruit_type: child?.fruit_type, layer_type: child?.type, layer: child });
    return toDays(match?.max_age_days);
};

export const toCheckLocations = (value: string | string[] | null | undefined): string[] => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
};

// * The backend stores the parent of each type a check was done for as parent_<type>_id.
// TODO(EFQ-4298): checks created before that have no parent_<type>_id and still count for every parent of their child.
// Drop this fallback (only keep checks stored for this layer) once those older checks no longer matter.
export const isCheckForLayer = (check: any, layer: any): boolean => {
    const parentId = check?.[`parent_${layer?.type}_id`];
    return !parentId || parentId === layer?.id;
};

const createdTime = (check: any): number => (check?.created ? Date.parse(check.created) : 0);

// * Newest first: by created, then by check_id
export const compareNewestFirst = (a: any, b: any): number => (createdTime(b) - createdTime(a)) || ((b?.check_id ?? 0) - (a?.check_id ?? 0));

const isWithinAge = (check: any, maxAgeDays?: number | null): boolean => (
    maxAgeDays == null || !check?.created || Date.now() - createdTime(check) <= maxAgeDays * DAY_MS
);

// * A layer with a facility only takes fallback checks done there; a check without a facility does not count
const isAtLayerFacility = (check: any, layerFacility: string | null | undefined, facilityOf?: CheckSelection["facilityOf"]): boolean => (
    !layerFacility || facilityOf?.(check) === layerFacility
);

/**
 * The selected checks per child layer id, newest first: its preferred checks, otherwise its fallback checks.
 * Without preferred and fallback locations every check counts as preferred.
 */
export const selectChecksPerLayer = (checks: any[] | null | undefined, selection: CheckSelection = {}): Map<string, any[]> => {
    const preferred = new Set(selection.preferred ?? []);
    const fallback = new Set(selection.fallback ?? []);
    const { layer, onlyForLayer, fallbackMaxAgeDays, facilityOf } = selection;
    const layerFacility = layer?.latest_location?.facility;
    const childById = new Map((selection.children ?? []).map((child) => [child.id, child]));
    const maxAgeByChild = new Map<string, number | null>();
    const maxAgeOf = (layerId: string) => {
        if (!maxAgeByChild.has(layerId)) maxAgeByChild.set(layerId, getFallbackMaxAgeDays(fallbackMaxAgeDays, childById.get(layerId), selection.applyDisplayFilterFields));
        return maxAgeByChild.get(layerId);
    };

    const isPreferred = (check) => (preferred.size ? preferred.has(check.location) : fallback.size === 0)
        && (!onlyForLayer || !layer || isCheckForLayer(check, layer));
    const isFallback = (check) => fallback.has(check.location)
        && isWithinAge(check, maxAgeOf(check.layer_id))
        && isAtLayerFacility(check, layerFacility, facilityOf);

    const byLayer = new Map<string, { preferred: any[]; fallback: any[] }>();
    (checks ?? []).forEach((check) => {
        const entry = byLayer.get(check.layer_id) ?? { preferred: [], fallback: [] };
        if (isPreferred(check)) entry.preferred.push(check);
        else if (isFallback(check)) entry.fallback.push(check);
        byLayer.set(check.layer_id, entry);
    });

    const selected = new Map<string, any[]>();
    byLayer.forEach((entry, layerId) => {
        const layerChecks = entry.preferred.length ? entry.preferred : entry.fallback;
        if (layerChecks.length) selected.set(layerId, [...layerChecks].sort(compareNewestFirst));
    });
    return selected;
};

// * The one check per child layer id a row shows: the newest selected check
export const selectLatestCheckPerLayer = (checks: any[] | null | undefined, selection: CheckSelection = {}): Map<string, any> => {
    const latest = new Map<string, any>();
    selectChecksPerLayer(checks, selection).forEach((layerChecks, layerId) => latest.set(layerId, layerChecks[0]));
    return latest;
};

// * Facility of a check from the tenant's facility locations (config.facility_locations)
export const getCheckFacility = (facilityLocations: any[] | null | undefined) => {
    const facilityById = new Map((facilityLocations ?? []).map((location) => [location.location_id, location.facility]));
    return (check: any) => (check?.location_id ? facilityById.get(check.location_id) : undefined);
};
